import { toEnglishDigits } from '@/modules/Core/utils/convertFaToEnNumbers'

export interface JalaliParts {
  year: number
  month: number
  day: number
}

export interface CalendarDayCell {
  day: number | null
  dateKey: string | null
  isFriday: boolean
  isToday: boolean
}

const PERSIAN_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
]

const PERSIAN_WEEKDAYS = [
  'یکشنبه',
  'دوشنبه',
  'سه‌شنبه',
  'چهارشنبه',
  'پنجشنبه',
  'جمعه',
  'شنبه',
]

export const WEEKDAY_LABELS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج']

const persianDateFormatter = new Intl.DateTimeFormat('en-US-u-ca-persian', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  timeZone: 'Asia/Tehran',
})

function parsePersianParts(date: Date): JalaliParts {
  const parts = persianDateFormatter.formatToParts(date)
  const year = Number(parts.find((part) => part.type === 'year')?.value)
  const month = Number(parts.find((part) => part.type === 'month')?.value)
  const day = Number(parts.find((part) => part.type === 'day')?.value)

  return { year, month, day }
}

export function getJalaliParts(date: Date | string = new Date()): JalaliParts {
  return parsePersianParts(typeof date === 'string' ? new Date(date) : date)
}

export function toDateKey(date: Date | string): string {
  const { year, month, day } = getJalaliParts(date)
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function getTodayKey(): string {
  return toDateKey(new Date())
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

export function startOfLocalDay(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function toLocalIsoDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Parses Jalali calendar date strings (e.g. 1403/07/20) to a local Gregorian Date. */
export function parseJalaliDateStringToDate(value: string): Date | null {
  const normalized = toEnglishDigits(value).replace(/[/.]/g, '-')
  const match = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)

  if (!match) {
    return null
  }

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])

  return findGregorianForJalali(year, month, day)
}

/** Same rules as letter form: Jalali string to API `YYYY-MM-DD` (Gregorian). */
export function parseJalaliDateStringToIsoDate(value: string): string | null {
  const gregorian = parseJalaliDateStringToDate(value)

  if (!gregorian) {
    return null
  }

  return toLocalIsoDateString(gregorian)
}

export function findGregorianForJalali(
  year: number,
  month: number,
  day: number
): Date {
  let low = new Date(year + 621, 0, 1).getTime()
  let high = new Date(year + 622, 11, 31).getTime()

  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const candidate = new Date(mid)
    const parts = parsePersianParts(candidate)
    const cmp =
      parts.year !== year
        ? parts.year - year
        : parts.month !== month
          ? parts.month - month
          : parts.day - day

    if (cmp < 0) {
      low = mid + 24 * 60 * 60 * 1000
    } else if (cmp > 0) {
      high = mid - 24 * 60 * 60 * 1000
    } else {
      return startOfLocalDay(candidate)
    }
  }

  return startOfLocalDay(new Date(low))
}

export function getDaysInJalaliMonth(year: number, month: number): number {
  if (month <= 6) {
    return 31
  }
  if (month <= 11) {
    return 30
  }

  const candidate = findGregorianForJalali(year, 12, 30)
  const parts = parsePersianParts(candidate)
  return parts.year === year && parts.month === 12 && parts.day === 30 ? 30 : 29
}

function jalaliWeekdayIndex(date: Date): number {
  return (date.getDay() + 1) % 7
}

export function buildMonthGrid(year: number, month: number): CalendarDayCell[] {
  const daysInMonth = getDaysInJalaliMonth(year, month)
  const firstDay = findGregorianForJalali(year, month, 1)
  const leadingEmpty = jalaliWeekdayIndex(firstDay)
  const todayKey = getTodayKey()
  const cells: CalendarDayCell[] = []

  for (let i = 0; i < leadingEmpty; i += 1) {
    cells.push({ day: null, dateKey: null, isFriday: false, isToday: false })
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = findGregorianForJalali(year, month, day)
    const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    cells.push({
      day,
      dateKey,
      isFriday: jalaliWeekdayIndex(date) === 6,
      isToday: dateKey === todayKey,
    })
  }

  while (cells.length % 7 !== 0) {
    cells.push({ day: null, dateKey: null, isFriday: false, isToday: false })
  }

  return cells
}

export function shiftJalaliMonth(
  year: number,
  month: number,
  delta: number
): JalaliParts {
  let nextYear = year
  let nextMonth = month + delta

  while (nextMonth > 12) {
    nextMonth -= 12
    nextYear += 1
  }
  while (nextMonth < 1) {
    nextMonth += 12
    nextYear -= 1
  }

  return { year: nextYear, month: nextMonth, day: 1 }
}

export function formatMonthTitle(year: number, month: number): string {
  return `${PERSIAN_MONTHS[month - 1]} ${toPersianDigits(year)}`
}

export function toPersianDigits(value: number | string): string {
  return String(value).replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)])
}

export function formatReminderDateLabel(date: string): string {
  const value = new Date(date)
  const weekday = PERSIAN_WEEKDAYS[value.getDay()]
  const formatted = value.toLocaleDateString('fa-IR', {
    calendar: 'persian',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Tehran',
  })

  return `${weekday} ${formatted}`
}

export function formatReminderScheduleLabel(
  date: string,
  time?: string
): string {
  const dateLabel = formatReminderDateLabel(date)
  if (!time) {
    return dateLabel
  }
  return `${dateLabel}، ${toPersianDigits(time)}`
}

export function formatGroupTitle(date: string): string {
  const key = toDateKey(date)
  const todayKey = getTodayKey()
  const tomorrowKey = toDateKey(addDays(startOfLocalDay(), 1))

  if (key === todayKey) {
    return 'امروز'
  }
  if (key === tomorrowKey) {
    return 'فردا'
  }

  const { year, month, day } = getJalaliParts(date)
  return `${toPersianDigits(day)} ${PERSIAN_MONTHS[month - 1]} ${toPersianDigits(year)}`
}

export function isSameJalaliDay(a: string, b: string): boolean {
  return toDateKey(a) === toDateKey(b)
}

export interface DateRange {
  from: string
  to: string
}

function toLocalDayRange(start: Date, end: Date): DateRange {
  const endOfDay = addDays(startOfLocalDay(end), 1)
  endOfDay.setMilliseconds(-1)
  return {
    from: startOfLocalDay(start).toISOString(),
    to: endOfDay.toISOString(),
  }
}

export function getDateKeyRange(dateKey: string): DateRange {
  const [year, month, day] = dateKey.split('-').map(Number)
  const date = findGregorianForJalali(year, month, day)
  return toLocalDayRange(date, date)
}

export function getJalaliMonthRange(year: number, month: number): DateRange {
  return toLocalDayRange(
    findGregorianForJalali(year, month, 1),
    findGregorianForJalali(year, month, getDaysInJalaliMonth(year, month))
  )
}

export function formatDateKeyLabel(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number)
  return `${toPersianDigits(day)} ${PERSIAN_MONTHS[month - 1]} ${toPersianDigits(year)}`
}

export function formatDateKeyLabelWithWeekday(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number)
  const gregorian = findGregorianForJalali(year, month, day)
  const weekday = PERSIAN_WEEKDAYS[gregorian.getDay()]
  return `${weekday}، ${toPersianDigits(day)} ${PERSIAN_MONTHS[month - 1]} ${toPersianDigits(year)}`
}
